"use client";

import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Assessment() {
  const headlineWords = ["Assessment", "is", "not", "a", "finality", ";", "it", "is", "a", "diagnostic."];
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: signatureRef,
    isVisible: signatureVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3383" id="future-of-assessment" className="bg-background text-foreground relative overflow-hidden min-h-screen flex items-center">
      {/* Flat CSS graph-paper grid pattern */}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
      backgroundSize: "32px 32px"
    }} />

      {/* Foreground Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-24 md:py-32 lg:py-40">
        <div className="max-w-4xl">
          {/* Decimal Index & Eyebrow */}
          <div ref={eyebrowRef} className={`flex items-center gap-4 mb-8 md:mb-10 transition-all duration-1000 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{
            color: "hsl(39 100% 80%)"
          }}>
              THE DIAGNOSTIC SHIFT
            </span>
            <span className="h-px w-16 md:w-24" style={{
            backgroundColor: "hsl(39 100% 80%)"
          }} />
          </div>

          {/* Staggered Revealing Headline */}
          <h2 ref={headlineRef} className="font-default font-semibold text-foreground mb-10 md:mb-12 max-w-[70ch]" style={{
          fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
          lineHeight: 1.05,
          letterSpacing: "-0.04em"
        }}>
            {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block mr-[0.25em] transition-all ease-out" style={{
            transitionDuration: "900ms",
            transitionDelay: headlineVisible ? `${i * 110}ms` : "0ms",
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(0.5em)"
          }}>
                {word}
              </span>)}
          </h2>

          {/* Narrative Body Copy */}
          <p ref={bodyRef} className={`font-default text-base md:text-lg leading-relaxed max-w-xl mb-10 md:mb-12 transition-all duration-1000 ease-out delay-500 ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          color: "hsl(254 10% 60%)"
        }}>
            Setura maps student responses directly to mark-scheme logic, identifying the precise process errors that cause marks to be lost. We move beyond simple scoring to reveal the mechanical failure points in student reasoning, providing actionable data for every step of the curriculum.
          </p>

          {/* Action CTAs */}
          <div ref={ctaRef} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 transition-all duration-1000 ease-out delay-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-white rounded-sm px-8 h-12 text-base font-medium shadow-lg">
              <Link to="/SeturaPage">
                Explore Setura
                <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="rounded-sm px-8 h-12 text-base font-medium bg-transparent hover:bg-white/5 border-border" style={{
            color: "hsl(0 0% 98%)"
          }}>
              <Link to="/PilotPage">
                <Phone className="mr-1 size-4" />
                Book a pilot call
              </Link>
            </Button>
          </div>
        </div>

        {/* Technical Coordinate Footnote / Axis Marker */}
        <div ref={signatureRef} className={`mt-20 md:mt-24 lg:mt-32 flex items-center justify-end gap-4 transition-all duration-1000 ease-out delay-1000 ${signatureVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="h-px w-20 md:w-32" style={{
          backgroundColor: "hsl(39 100% 80%)"
        }} />
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{
          color: "hsl(39 100% 80%)"
        }}>
            RESSETI · DIAGNOSTIC ENGINE
          </span>
        </div>
      </div>
    </section>;
}